import { useEffect, useState } from 'react';
import { Diamond } from 'lucide-react';
import { useCanvasStore } from '../../store/canvas-store';
import { usePlayheadTime } from './usePlayheadTime';
import { sampleValueAt } from './sampleValueAt';
import { snapTime } from './timeMap';
import type { MotionTimeline, MotionProp } from '../../store/types';

const COLOR_PROPS: MotionProp[] = ['color', 'backgroundColor'];

/** Live sampled value at the playhead; editing keys the typed value there. */
export function ChannelValueField({
  frameId,
  timeline,
  trackId,
  layerId,
  prop,
}: {
  frameId: string;
  timeline: MotionTimeline;
  trackId: string;
  layerId: string;
  prop: MotionProp;
}) {
  const dispatch = useCanvasStore((s) => s.dispatch);
  const t = usePlayheadTime(frameId);
  const sampled = sampleValueAt(timeline, trackId, layerId, prop, t);
  const isColor = COLOR_PROPS.includes(prop);
  const [draft, setDraft] = useState<string | null>(null); // null = mirror sampled

  // A moving playhead refreshes the display unless the user is mid-edit.
  useEffect(() => {
    if (draft !== null && document.activeElement?.getAttribute('aria-label') !== `${prop} value`) setDraft(null);
  }, [t]); // eslint-disable-line react-hooks/exhaustive-deps

  const keyAt = (value: number | string) =>
    dispatch({
      t: 'motion.setKeyframe',
      frameId,
      trackId,
      layerId,
      prop,
      kf: { t: snapTime(t, timeline.fps), value, ease: 'easeInOut' },
    });

  const commit = () => {
    if (draft === null) return;
    const v: number | string = isColor ? draft : Number(draft);
    if (!isColor && Number.isNaN(v as number)) {
      setDraft(null);
      return;
    }
    keyAt(v);
    setDraft(null);
  };

  const display = draft ?? (typeof sampled === 'number' ? String(Math.round(sampled * 100) / 100) : String(sampled));

  return (
    <span className="ml-auto flex items-center gap-1">
      {isColor && !String(display).includes('var(') ? (
        <input
          aria-label={`${prop} value`}
          type="color"
          value={String(display)}
          className="h-4 w-6 cursor-pointer border-0 bg-transparent p-0"
          onChange={(e) => {
            setDraft(e.target.value);
            keyAt(e.target.value);
          }}
        />
      ) : (
        <input
          aria-label={`${prop} value`}
          value={display}
          size={6}
          className="w-14 rounded border border-transparent bg-transparent px-1 text-right tabular-nums hover:border-[var(--color-chip-border)] focus:border-[var(--color-accent)] focus:outline-none"
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') setDraft(null);
          }}
        />
      )}
      <button
        aria-label="Add keyframe at playhead"
        title="Keyframe current value at playhead"
        className="hover:text-[var(--color-accent)]"
        onClick={() => keyAt(sampled)}
      >
        <Diamond size={9} className="rotate-0" />
      </button>
    </span>
  );
}
